import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tab } from '@headlessui/react';

interface MetadataField {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'select' | 'boolean';
  required: boolean;
  options?: string[]; // Para campos do tipo select
  defaultValue?: string;
}

interface WorkflowStep {
  id: string;
  name: string;
  description: string;
  approvers: {
    type: 'user' | 'team';
    id: string;
    name: string;
  }[];
  order: number;
  timeLimit?: number; // Em horas
}

export const DocumentTypeFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  // Estados para os diferentes aspectos do tipo de documento
  const [basicInfo, setBasicInfo] = useState({
    name: '',
    description: '',
    status: 'active' as 'active' | 'inactive'
  });

  const [metadataFields, setMetadataFields] = useState<MetadataField[]>([]);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [layout, setLayout] = useState(''); // Conteúdo do editor WYSIWYG

  // Estado para controle de abas
  const [selectedTab, setSelectedTab] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Implementar lógica de salvamento
    navigate('/admin/documents/types');
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Editar Tipo de Documento' : 'Novo Tipo de Documento'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Configure todos os aspectos do tipo de documento, incluindo layout, metadados e workflow.
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => navigate('/admin/documents/types')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="document-type-form"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Salvar
            </button>
          </div>
        </div>

        <form id="document-type-form" onSubmit={handleSubmit}>
          <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
            <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1">
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700
                  ${selected ? 'bg-white shadow' : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'}`
                }
              >
                Informações Básicas
              </Tab>
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700
                  ${selected ? 'bg-white shadow' : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'}`
                }
              >
                Layout do Documento
              </Tab>
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700
                  ${selected ? 'bg-white shadow' : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'}`
                }
              >
                Metadados
              </Tab>
              <Tab
                className={({ selected }) =>
                  `w-full rounded-lg py-2.5 text-sm font-medium leading-5 text-blue-700
                  ${selected ? 'bg-white shadow' : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'}`
                }
              >
                Workflow
              </Tab>
            </Tab.List>

            <Tab.Panels className="mt-6">
              {/* Painel de Informações Básicas */}
              <Tab.Panel>
                <div className="space-y-6 bg-white p-6 rounded-lg shadow">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Nome
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={basicInfo.name}
                      onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Descrição
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows={3}
                      value={basicInfo.description}
                      onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={basicInfo.status}
                      onChange={(e) => setBasicInfo({ ...basicInfo, status: e.target.value as 'active' | 'inactive' })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                      <option value="active">Ativo</option>
                      <option value="inactive">Inativo</option>
                    </select>
                  </div>
                </div>
              </Tab.Panel>

              {/* Painel de Layout do Documento */}
              <Tab.Panel>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Editor de Layout</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Use o editor abaixo para criar o layout padrão para este tipo de documento.
                    </p>
                  </div>
                  {/* Aqui será integrado o editor WYSIWYG (TinyMCE, CKEditor, etc.) */}
                  <div className="border rounded-md p-4 min-h-[400px]">
                    <p className="text-gray-500">Editor WYSIWYG será implementado aqui</p>
                  </div>
                </div>
              </Tab.Panel>

              {/* Painel de Metadados */}
              <Tab.Panel>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Campos de Metadados</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Configure os campos de metadados que serão solicitados ao criar documentos deste tipo.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {/* Implementar adição de campo */}}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Adicionar Campo
                    </button>
                  </div>
                  
                  <div className="mt-6">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nome do Campo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Obrigatório
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Lista de campos de metadados */}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Tab.Panel>

              {/* Painel de Workflow */}
              <Tab.Panel>
                <div className="bg-white p-6 rounded-lg shadow">
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Etapas do Workflow</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Configure as etapas de aprovação para documentos deste tipo.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {/* Implementar adição de etapa */}}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Adicionar Etapa
                    </button>
                  </div>

                  <div className="mt-6 space-y-4">
                    {/* Lista de etapas do workflow */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <p className="text-sm text-gray-500">
                        Arraste as etapas para reordenar o fluxo de aprovação.
                      </p>
                    </div>
                  </div>
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </form>
      </div>
    </div>
  );
}; 